// src/components/PodcastPlayer.jsx
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Play, Pause, SkipBack, SkipForward, Volume2, X, VolumeX, Clock } from 'lucide-react'
import useStore from '../store/useStore'
import Toast from './Toast'
import { globalAudio } from '../utils/globalAudio'

function PodcastPlayer() {
  const { 
    currentlyPlaying, 
    setCurrentlyPlaying, 
    addPodcastListening,
    user,
    addToast
  } = useStore()

  const [playerState, setPlayerState] = useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    progress: 0,
    volume: 70,
    isLoading: false,
    showControls: false
  })

  const [pointsEarned, setPointsEarned] = useState(0)
  const [showPointsNotification, setShowPointsNotification] = useState(false)
  
  // Store the last known state to detect hardware control changes
  const lastAudioState = useRef({
    isPlaying: false,
    currentPodcast: null
  })

  // Ref to store hardware control handlers
  const mediaSessionHandlers = useRef({})
  
  // Track if we've already saved listening progress for this session
  const hasSavedProgress = useRef(false)

  // FIX: Ultra-aggressive sync with audio element - runs constantly
  useEffect(() => {
    // Function to force sync with actual audio state
    const forceSyncWithAudio = () => {
      const audioState = globalAudio.getState();
      if (audioState && audioState.audioElement) {
        const isActuallyPlaying = !audioState.audioElement.paused && !audioState.audioElement.ended;
        const progress = audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0;
        
        // Always update state to match actual audio - this is the key fix
        setPlayerState(prev => {
          // Check if anything changed
          if (prev.isPlaying !== isActuallyPlaying || 
              Math.abs(prev.currentTime - audioState.currentTime) > 0.5 ||
              Math.abs(prev.progress - progress) > 1) {
            return {
              ...prev,
              isPlaying: isActuallyPlaying,
              currentTime: audioState.currentTime,
              duration: audioState.duration,
              progress: progress
            };
          }
          return prev;
        });
        
        lastAudioState.current.isPlaying = isActuallyPlaying;
      }
    };

    // Sync immediately
    forceSyncWithAudio();

    // Set up a VERY frequent sync interval (every 50ms) - like the Dashboard does
    const syncInterval = setInterval(forceSyncWithAudio, 50);

    return () => clearInterval(syncInterval);
  }, []); // Empty dependency array - run once on mount

  // Handle visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // When tab becomes visible again, force immediate sync multiple times
        const audioState = globalAudio.getState();
        if (audioState && audioState.audioElement) {
          // Check the actual state of the audio element
          const isActuallyPlaying = !audioState.audioElement.paused && !audioState.audioElement.ended;
          
          // Update player state to match actual audio state
          const progress = audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0;
          setPlayerState(prev => ({
            ...prev,
            isPlaying: isActuallyPlaying,
            currentTime: audioState.currentTime,
            duration: audioState.duration,
            progress: progress
          }));
          
          lastAudioState.current.isPlaying = isActuallyPlaying;
        }
        
        // Force multiple syncs over the next second
        for (let i = 1; i <= 5; i++) {
          setTimeout(() => {
            const audioState = globalAudio.getState();
            if (audioState && audioState.audioElement) {
              const isActuallyPlaying = !audioState.audioElement.paused && !audioState.audioElement.ended;
              setPlayerState(prev => ({
                ...prev,
                isPlaying: isActuallyPlaying,
                currentTime: audioState.currentTime,
                progress: audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0
              }));
            }
          }, i * 100);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // FIX: Add mutation observer to detect when component re-renders due to navigation
  useEffect(() => {
    // When component mounts or updates, force sync
    const audioState = globalAudio.getState();
    if (audioState && audioState.audioElement) {
      const isActuallyPlaying = !audioState.audioElement.paused && !audioState.audioElement.ended;
      const progress = audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0;
      
      setPlayerState(prev => ({
        ...prev,
        isPlaying: isActuallyPlaying,
        currentTime: audioState.currentTime,
        duration: audioState.duration,
        progress: progress
      }));
    }
  }, []); // Run once on mount, but also when component re-mounts due to navigation

  // Setup global audio listeners
  useEffect(() => {
    const handleTimeUpdate = (data) => {
      const progress = data.duration > 0 ? (data.currentTime / data.duration) * 100 : 0;
      setPlayerState(prev => ({
        ...prev,
        currentTime: data.currentTime,
        duration: data.duration,
        progress: progress
      }));
      
      // Auto-save progress every 30 seconds
      if (user && data.currentPodcast && data.currentTime > 0) {
        const shouldSave = 
          Math.floor(data.currentTime) % 30 === 0 &&
          Math.floor(data.currentTime) > 0 &&
          !hasSavedProgress.current;
        
        if (shouldSave) {
          hasSavedProgress.current = true;
          setTimeout(() => { hasSavedProgress.current = false; }, 1000);
          
          // Save progress to backend
          saveListeningProgress(data.currentPodcast.id, data.currentTime, false);
        }
      }
    };

    const handleEnded = () => {
      setPlayerState(prev => ({ ...prev, isPlaying: false, progress: 0, currentTime: 0 }));
      lastAudioState.current.isPlaying = false;
      
      const audioState = globalAudio.getState();
      if (user && audioState.currentPodcast) {
        const totalMinutes = audioState.duration / 60;
        const earnedPoints = addPodcastListening(audioState.currentPodcast, totalMinutes, true);
        if (earnedPoints > 0) {
          setPointsEarned(earnedPoints);
          setShowPointsNotification(true);
          setTimeout(() => setShowPointsNotification(false), 2000);
        }
        
        // Save completed podcast to backend
        saveListeningProgress(audioState.currentPodcast.id, audioState.duration, true);
      }
    };

    const handleLoadedMetadata = (data) => {
      setPlayerState(prev => ({ 
        ...prev, 
        duration: data.duration,
        isLoading: false 
      }));
    };

    const handleCanPlay = () => {
      setPlayerState(prev => ({ ...prev, isLoading: false }));
    };

    const handleWaiting = () => {
      setPlayerState(prev => ({ ...prev, isLoading: true }));
    };

    const handlePlaying = () => {
      setPlayerState(prev => ({ ...prev, isLoading: false, isPlaying: true }));
      lastAudioState.current.isPlaying = true;
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
      }
    };

    const handlePause = () => {
      setPlayerState(prev => ({ ...prev, isLoading: false, isPlaying: false }));
      lastAudioState.current.isPlaying = false;
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
      
      // Save progress when paused
      const audioState = globalAudio.getState();
      if (user && audioState.currentPodcast && audioState.currentTime > 0) {
        saveListeningProgress(audioState.currentPodcast.id, audioState.currentTime, false);
      }
    };

    const handleError = (data) => {
      setPlayerState(prev => ({ ...prev, isLoading: false, isPlaying: false }));
      lastAudioState.current.isPlaying = false;
      addToast({
        message: 'Error playing podcast.',
        type: 'error'
      });
    };

    const handleStopped = () => {
      setPlayerState(prev => ({ 
        ...prev, 
        isPlaying: false,
        currentTime: 0,
        progress: 0
      }));
      lastAudioState.current.isPlaying = false;
      lastAudioState.current.currentPodcast = null;
      setCurrentlyPlaying(null);
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
      }
    };

    // Subscribe to global audio events
    globalAudio.on('timeupdate', handleTimeUpdate);
    globalAudio.on('ended', handleEnded);
    globalAudio.on('loadedmetadata', handleLoadedMetadata);
    globalAudio.on('canplay', handleCanPlay);
    globalAudio.on('waiting', handleWaiting);
    globalAudio.on('playing', handlePlaying);
    globalAudio.on('pause', handlePause);
    globalAudio.on('error', handleError);
    globalAudio.on('stopped', handleStopped);

    // Initialize with current state
    const currentState = globalAudio.getState();
    if (currentState) {
      const progress = currentState.duration > 0 ? (currentState.currentTime / currentState.duration) * 100 : 0;
      setPlayerState(prev => ({
        ...prev,
        isPlaying: currentState.isPlaying,
        currentTime: currentState.currentTime,
        duration: currentState.duration,
        progress: progress,
        volume: currentState.volume
      }));
      
      lastAudioState.current = {
        isPlaying: currentState.isPlaying,
        currentPodcast: currentState.currentPodcast
      };
      
      if (currentState.currentPodcast && !currentlyPlaying) {
        setCurrentlyPlaying(currentState.currentPodcast);
      }
    }

    // Define hardware control handlers
    mediaSessionHandlers.current.handlePlay = () => {
      const audioState = globalAudio.getState();
      if (!audioState.currentPodcast) return;
      
      if (audioState.audioElement && !audioState.audioElement.paused) {
        return;
      }
      
      globalAudio.play().then(() => {
        setPlayerState(prev => ({ ...prev, isPlaying: true }));
        lastAudioState.current.isPlaying = true;
      }).catch(error => {
        console.log('Failed to resume from hardware:', error);
      });
    };

    mediaSessionHandlers.current.handlePause = () => {
      globalAudio.pause();
      setPlayerState(prev => ({ ...prev, isPlaying: false }));
      lastAudioState.current.isPlaying = false;
      
      // Save progress when paused via hardware
      const audioState = globalAudio.getState();
      if (user && audioState.currentPodcast && audioState.currentTime > 0) {
        saveListeningProgress(audioState.currentPodcast.id, audioState.currentTime, false);
      }
    };

    // Set up Media Session API
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', mediaSessionHandlers.current.handlePlay);
      navigator.mediaSession.setActionHandler('pause', mediaSessionHandlers.current.handlePause);
      navigator.mediaSession.setActionHandler('stop', () => {
        globalAudio.stop();
      });
      navigator.mediaSession.setActionHandler('seekbackward', () => {
        globalAudio.skipBackward();
      });
      navigator.mediaSession.setActionHandler('seekforward', () => {
        globalAudio.skipForward();
      });
    }

    return () => {
      globalAudio.off('timeupdate', handleTimeUpdate);
      globalAudio.off('ended', handleEnded);
      globalAudio.off('loadedmetadata', handleLoadedMetadata);
      globalAudio.off('canplay', handleCanPlay);
      globalAudio.off('waiting', handleWaiting);
      globalAudio.off('playing', handlePlaying);
      globalAudio.off('pause', handlePause);
      globalAudio.off('error', handleError);
      globalAudio.off('stopped', handleStopped);
      
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('stop', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
      }
    };
  }, [user, currentlyPlaying, addPodcastListening, addToast, setCurrentlyPlaying]);

  // Handle when currentlyPlaying changes
  useEffect(() => {
    if (!currentlyPlaying) return;
    
    const audioState = globalAudio.getState();
    
    if (audioState.currentPodcast?.id === currentlyPlaying.id) {
      const progress = audioState.duration > 0 ? (audioState.currentTime / audioState.duration) * 100 : 0;
      setPlayerState(prev => ({
        ...prev,
        isPlaying: audioState.isPlaying,
        currentTime: audioState.currentTime,
        duration: audioState.duration,
        progress: progress
      }));
      return;
    }
    
    if (audioState.isPlaying) {
      globalAudio.pause();
    }
    
    setPlayerState(prev => ({
      ...prev,
      progress: 0,
      currentTime: 0,
      isPlaying: false,
      isLoading: true,
      duration: 0
    }));
    
    globalAudio.load(currentlyPlaying, 0);
    
    const playFromBeginning = () => {
      globalAudio.play().then(() => {
        setPlayerState(prev => ({
          ...prev,
          isPlaying: true,
          isLoading: false,
          progress: 0,
          currentTime: 0
        }));
        lastAudioState.current = {
          isPlaying: true,
          currentPodcast: currentlyPlaying
        };
        
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: currentlyPlaying.title,
            artist: 'Saints Academy',
            album: currentlyPlaying.topic || 'Podcast',
            artwork: [
              { src: currentlyPlaying.image, sizes: '96x96', type: 'image/jpeg' },
              { src: currentlyPlaying.image, sizes: '128x128', type: 'image/jpeg' },
              { src: currentlyPlaying.image, sizes: '192x192', type: 'image/jpeg' },
              { src: currentlyPlaying.image, sizes: '256x256', type: 'image/jpeg' },
              { src: currentlyPlaying.image, sizes: '384x384', type: 'image/jpeg' },
              { src: currentlyPlaying.image, sizes: '512x512', type: 'image/jpeg' },
            ]
          });
          navigator.mediaSession.playbackState = 'playing';
        }
      }).catch(error => {
        setPlayerState(prev => ({
          ...prev,
          isLoading: false
        }));
      });
    };
    
    const waitForLoad = () => {
      const state = globalAudio.getState();
      if (state.duration > 0) {
        if (state.currentTime > 0) {
          globalAudio.seek(0);
        }
        playFromBeginning();
      } else {
        setTimeout(waitForLoad, 100);
      }
    };
    
    setTimeout(waitForLoad, 50);

  }, [currentlyPlaying?.id]);

  useEffect(() => {
    globalAudio.setVolume(playerState.volume);
  }, []);

  // Function to save listening progress to backend
  const saveListeningProgress = async (podcastId, currentTime, completed) => {
    if (!user) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const progress = Math.floor((currentTime / playerState.duration) * 100) || 0;
      
      await fetch(`/api/podcasts/${podcastId}/listen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          progress: progress,
          completed: completed,
          currentTime: Math.floor(currentTime)
        })
      });
    } catch (error) {
      console.error('Failed to save listening progress:', error);
    }
  };

  const togglePlay = () => {
    const audioState = globalAudio.getState();
    const podcastToPlay = currentlyPlaying || audioState.currentPodcast;
    
    if (!podcastToPlay) return;
    
    if (playerState.isPlaying) {
      globalAudio.pause();
    } else {
      if (playerState.progress >= 100 || playerState.currentTime === 0) {
        globalAudio.seek(0);
      }
      globalAudio.play().then(() => {
        setPlayerState(prev => ({ ...prev, isPlaying: true }));
        lastAudioState.current.isPlaying = true;
      }).catch(error => {
        console.log('Play failed:', error);
        addToast({ message: 'Click play to start', type: 'info' });
      });
    }
  };

  const handleSeek = (e) => {
    if (playerState.duration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * playerState.duration;
    globalAudio.seek(newTime);
  };

  const skipForward = () => globalAudio.skipForward();
  const skipBackward = () => globalAudio.skipBackward();

  const handleVolumeChange = (e) => {
    const volume = parseInt(e.target.value);
    globalAudio.setVolume(volume);
    setPlayerState(prev => ({ ...prev, volume }));
  };

  const handleVolumeToggle = () => {
    const newVolume = playerState.volume === 0 ? 70 : 0;
    globalAudio.setVolume(newVolume);
    setPlayerState(prev => ({ ...prev, volume: newVolume }));
  };

  const handleStop = () => {
    globalAudio.stop();
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null;
    }
    setCurrentlyPlaying(null);
    addToast({ message: 'Stopped', type: 'info' });
  };

  const toggleControls = () => {
    setPlayerState(prev => ({ ...prev, showControls: !prev.showControls }));
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentPodcast = currentlyPlaying || globalAudio.getState()?.currentPodcast;
  if (!currentPodcast) return null;

  const circumference = 2 * Math.PI * 48;

  return (
    <>
      <Toast 
        message="Podcast completed!"
        points={pointsEarned}
        type="points"
        isVisible={showPointsNotification}
        onHide={() => setShowPointsNotification(false)}
      />

      <AnimatePresence>
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: -10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -10 }}
          className="fixed top-20 right-4 z-40"
          style={{ width: '150px' }}
        >
          <div className="w-40 bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl border border-gray-800 overflow-hidden">
            
            {/* Top Bar with X */}
            <div className="flex items-center justify-between px-3 py-2 bg-black/50 backdrop-blur-sm">
              <span className="text-[10px] text-gray-400 truncate">
                {currentPodcast.topic || 'Podcast'}
              </span>
              <button
                onClick={handleStop}
                className="p-1 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            
            {/* Center Image */}
            <div className="p-2">
              <div 
                className="relative w-36 h-36 rounded-lg overflow-hidden mx-auto group cursor-pointer"
                onClick={togglePlay}
              >
                <img 
                  src={currentPodcast.image} 
                  alt={currentPodcast.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                
                {/* Play Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    {playerState.isPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white" />
                    )}
                  </div>
                </div>
                
                {/* Progress Ring */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="48"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="2"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="48"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference - (playerState.progress / 100) * circumference}
                    />
                  </svg>
                </div>
                
                {/* Current Time */}
                <div className="absolute bottom-1 right-1 bg-black/70 backdrop-blur-sm rounded-full px-1.5 py-0.5">
                  <span className="text-[10px] text-white font-medium">
                    {formatTime(playerState.currentTime)}
                  </span>
                </div>
              </div>
              
              {/* Title and Total Duration */}
              <div className="mt-2 text-center">
                <h4 className="font-semibold text-white text-xs truncate px-1 mb-0.5">
                  {currentPodcast.title}
                </h4>
                <div className="flex items-center justify-center gap-1.5">
                  <p className="text-[10px] text-gray-400">
                    {currentPodcast.episodeType}
                    {playerState.isPlaying && ' • Now Playing'}
                  </p>
                  {playerState.duration > 0 && (
                    <div className="flex items-center gap-0.5 text-[10px] text-gray-500">
                      <Clock className="w-2.5 h-2.5" />
                      <span>{formatTime(playerState.duration)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Expandable Controls */}
            <AnimatePresence>
              {playerState.showControls && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-2 pb-2 space-y-3">
                    {/* Progress Bar */}
                    <div 
                      className="w-full h-1 bg-gray-800 rounded-full overflow-hidden cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                        style={{ width: `${playerState.progress}%` }}
                      />
                    </div>
                    
                    {/* Skip Controls with Simple Labels */}
                    <div className="flex items-center justify-center space-x-6">
                      <div className="flex flex-col items-center">
                        <button 
                          className="p-1 text-gray-300 hover:text-white transition-colors mb-1"
                          onClick={skipBackward}
                        >
                          <SkipBack className="w-5 h-5" />
                        </button>
                        <span className="text-[9px] text-gray-400">-15s</span>
                      </div>
                      
                      <button
                        onClick={togglePlay}
                        className="p-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg"
                        disabled={playerState.isLoading}
                      >
                        {playerState.isLoading ? (
                          <span className="w-5 h-5 flex items-center justify-center">...</span>
                        ) : playerState.isPlaying ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </button>
                      
                      <div className="flex flex-col items-center">
                        <button 
                          className="p-1 text-gray-300 hover:text-white transition-colors mb-1"
                          onClick={skipForward}
                        >
                          <SkipForward className="w-5 h-5" />
                        </button>
                        <span className="text-[9px] text-gray-400">+15s</span>
                      </div>
                    </div>
                    
                    {/* Volume */}
                    <div className="px-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="relative group">
                          <button 
                            onClick={handleVolumeToggle}
                            className="p-0.5 text-gray-300 hover:text-white transition-colors"
                          >
                            {playerState.volume === 0 ? 
                              <VolumeX className="w-3.5 h-3.5" /> : 
                              <Volume2 className="w-3.5 h-3.5" />
                            }
                          </button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10 border border-gray-700 shadow-lg">
                            {playerState.volume === 0 ? 'Unmute' : 'Mute'}
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-300">
                          {playerState.volume}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={playerState.volume}
                        onChange={handleVolumeChange}
                        className="w-full h-1.5 accent-blue-500 bg-gray-700 rounded-lg cursor-pointer"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Toggle Controls */}
            <div className="px-2 pb-2">
              <button
                onClick={toggleControls}
                className="w-full py-1.5 bg-gray-800/50 hover:bg-gray-800 rounded-lg text-[10px] text-gray-300 hover:text-white transition-colors flex items-center justify-center"
              >
                {playerState.showControls ? 'Hide Controls' : 'Show Controls'}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  )
}

export default PodcastPlayer